import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { Product } from '../types';
import { inventoryApi } from '../services/api';

export const useInventoryStore = defineStore('inventory', () => {
  const products = ref<Product[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  async function fetchProducts() {
    isLoading.value = true;
    error.value = null;
    try {
      products.value = await inventoryApi.getProducts();
    } catch (e) {
      error.value = 'Failed to fetch products.';
    } finally {
      isLoading.value = false;
    }
  }

  async function addProduct(product: Omit<Product, 'id'>) {
    isLoading.value = true;
    error.value = null;
    try {
      const newProduct = await inventoryApi.addProduct(product);
      products.value.push(newProduct);
    } catch (e) {
      error.value = 'Failed to add product.';
    } finally {
      isLoading.value = false;
    }
  }

  async function updateProduct(updatedProduct: Product) {
    isLoading.value = true;
    error.value = null;
    try {
      const returnedProduct = await inventoryApi.updateProduct(updatedProduct);
      const index = products.value.findIndex(p => p.id === returnedProduct.id);
      if (index !== -1) {
        products.value[index] = returnedProduct;
      }
    } catch (e) {
      error.value = 'Failed to update product.';
    } finally {
      isLoading.value = false;
    }
  }

  async function updateStock(productId: string, change: number) {
    // This can be a lighter operation, so we might not set global loading
    try {
        // Stock updated silently
      const updatedProduct = await inventoryApi.updateProductStock(productId, change);
      const index = products.value.findIndex(p => p.id === updatedProduct.id);
      if (index !== -1) {
        products.value[index] = updatedProduct;
      }
    } catch (e) {
      // Handle stock update error, maybe revert optimistic update in UI
      console.error('Failed to update stock:', e);
    }
  }

  async function deleteProduct(id: string) {
    isLoading.value = true;
    error.value = null;
    try {
      await inventoryApi.deleteProduct(id);
      products.value = products.value.filter(p => p.id !== id);
    } catch (e) {
      error.value = 'Failed to delete product.';
    } finally {
      isLoading.value = false;
    }
  }

  function getProduct(productId: string) {
    return products.value.find(p => p.id === productId);
  }

  return {
    products,
    isLoading,
    error,
    fetchProducts,
    addProduct,
    updateProduct,
    updateStock,
    deleteProduct,
    getProduct
  };
});
